import { inject, NgModule } from '@angular/core';
import { provideRouter, RouterModule } from '@angular/router';
import { CheckoutLayoutComponent } from '@pages/checkout-app/checkout-layout/checkout-layout.component';
import { CheckoutListComponent } from '@pages/checkout-app/checkout-layout/main/components/delivery-rates/checkout-list/checkout-list';
import { CheckoutExtensionsComponent } from '@pages/checkout-app/checkout-layout/main/components/extensions/extensions.component';
import { ExtensionGroupsComponent } from '@pages/checkout-app/checkout-layout/main/components/extension-groups/extension-groups.component';
import { AbtestComponent } from '@pages/checkout-app/components/abtest/abtest.component';
import { AbTestingContainerComponent } from './checkout-layout/main/components/ab-testing/ab-testing-container.component';
import { AddCheckout } from './components/form-actions/add-checkout/add-checkout';
import { EditCheckout } from './components/form-actions/edit-checkout/edit-checkout';
import { CanDeactivateGuard } from './shared/deactivate-guard/deactivate-gurad';
import { NavigateAwayGuard } from './shared/navigate-away-guard/navigate-away-guard';
import { extensionTypeAccessGuard, featureAccessGuard } from '@app/core/guards/feature-access.guard';
import { FeatureKey } from '@pdqdev/pdq-shared/dist/utils/features-manifest';
import { ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { MultiShipmentEnabledGuard } from '@app/core/guards/multi-shipment-enabled.guard';
import { CustomUpsellComponent } from './checkout-layout/main/components/extensions/components/extension-components/custom-upsell/custom-upsell.component';
import { DatePickerComponent } from './checkout-layout/main/components/extensions/components/extension-components/date-picker/date-picker.component';
import { CartControlComponent } from './checkout-layout/main/components/extensions/components/extension-components/cart-control/cart-control.component';
import { HooxPayComponent } from './checkout-layout/main/components/extensions/components/extension-components/embedded-offers/hooxpay/hoox-pay.component';
import { ShippingRatesFilterComponent } from './checkout-layout/main/components/extensions/components/extension-components/shipping-rates-filter/shipping-rates-filter.component';
import { GiftNoteComponent } from './checkout-layout/main/components/extensions/components/extension-components/gift-note/gift-note.component';
import { GiftUnlockComponent } from './checkout-layout/main/components/extensions/components/extension-components/gift-unlock/gift-unlock.component';
import { CheckoutContentComponent } from './checkout-layout/main/components/extensions/components/extension-components/checkout-content/checkout-content.component';
import { DeliveryCountdownTimerComponent } from './checkout-layout/main/components/extensions/components/extension-components/delivery-countdown-timer/delivery-countdown-timer.component';
import { CheckoutUpsellComponent } from './checkout-layout/main/components/extensions/components/extension-components/checkout-upsell/checkout-upsell.component';
import { CheckoutExtensionsListComponent } from './checkout-layout/main/components/extensions/list/checkout-extensions-list.component';
import { VipProcessComponent } from './checkout-layout/main/components/extensions/components/extension-components/vip-process/vip-process.component';
import { DiscountRejectionCouponComponent } from './checkout-layout/main/components/extensions/components/extension-components/discount-rejection-coupon/discount-rejection-coupon.component';
import { DynamicReviewsComponent } from './checkout-layout/main/components/extensions/components/extension-components/dynamic-reviews/dynamic-reviews.component';
import { ProductUpgradeComponent } from './checkout-layout/main/components/extensions/components/extension-components/product-upgrade/product-upgrade.component';
import { UrgencyBoostComponent } from './checkout-layout/main/components/extensions/components/extension-components/urgency-boost/urgency-boost.component';
import { MultiShipmentComponent } from './checkout-layout/main/components/extensions/components/extension-components/multi-shipment/multi-shipment.component';
import { DynamicExtensionComponent } from './checkout-layout/main/components/extensions/components/extension-components/dynamic-extension/dynamic-extension.component';
import { OrderProtectionComponent } from './checkout-layout/main/components/extensions/components/extension-components/order-protection/order-protection.component';

@NgModule({
  imports: [],
  providers: [
    CanDeactivateGuard,
    NavigateAwayGuard,
    provideRouter([
      // TODO: Move AbTestingContainerComponent to /app/pages/ab-testing/ module
      // This component belongs under "Optimize" not "Convert/Checkout"
      // Create: /app/pages/ab-testing/ab-testing.module.ts
      // Then lazy load from /optimize/ab-tests in app-routing.module.ts
      {
        title: 'A/B tests',
        path: '',
        pathMatch: 'full',
        component: AbTestingContainerComponent,
      },
      {
        path: '',
        component: CheckoutLayoutComponent,
        children: [
          {
            title: 'Shipping rate groups',
            path: 'checkout-groups',
            canActivate: [featureAccessGuard(FeatureKey.CHECKOUT_GROUPS)],
            canDeactivate: [() => inject(NavigateAwayGuard).canDeactivate()],
            children: [
              {
                path: 'methods',
                children: [
                  {
                    path: 'new',
                    component: AddCheckout,
                    canDeactivate: [() => inject(CanDeactivateGuard).canDeactivate()],
                  },
                  {
                    path: 'list',
                    component: CheckoutListComponent,
                  },
                  {
                    path: 'edit',
                    component: EditCheckout,
                    canDeactivate: [(page: EditCheckout) => inject(CanDeactivateGuard).canDeactivate(page)],
                  },
                  { path: '**', redirectTo: 'list', pathMatch: 'full' },
                ],
              },
              { path: '**', redirectTo: 'methods', pathMatch: 'full' },
            ],
          },
          {
            title: 'Extension groups - Beta',
            path: 'extension-groups/:id',
            component: ExtensionGroupsComponent,
            canActivate: [featureAccessGuard(FeatureKey.EXTENSION_GROUPS)],
          },
          {
            title: 'Extension groups - Beta',
            path: 'extension-groups',
            component: ExtensionGroupsComponent,
            canActivate: [featureAccessGuard(FeatureKey.EXTENSION_GROUPS)],
          },
          {
            title: 'A/B testing',
            path: 'abtest',
            component: AbtestComponent,
          },
          {
            title: 'Extensions',
            path: 'extensions',
            component: CheckoutExtensionsComponent,
            canActivate: [featureAccessGuard(FeatureKey.EXTENSIONS)],
            children: [
              {
                path: '',
                component: CheckoutExtensionsListComponent,
              },
              {
                title: 'Date Picker',
                path: 'date-picker/:componentId',
                component: DatePickerComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.DATE_PICKER)],
              },
              {
                title: 'Upsell',
                path: 'upsell/:usageToken',
                component: CheckoutUpsellComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.CHECKOUT_UPSELL)],
              },
              {
                title: 'Custom Upsell',
                path: 'custom-upsell/:usageToken',
                component: CustomUpsellComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.CUSTOM_UPSELL)],
              },
              {
                title: 'Cart Control',
                path: 'cart-control/:usageToken',
                component: CartControlComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.CART_CONTROL)],
              },
              {
                title: 'Delivery Countdown Timer',
                path: 'delivery-countdown-timer/:usageToken',
                component: DeliveryCountdownTimerComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.DELIVERY_COUNTDOWN_TIMER)],
              },
              {
                title: 'Checkout Content',
                path: 'checkout-content/:usageToken',
                component: CheckoutContentComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.CHECKOUT_CONTENT)],
              },
              {
                title: 'Gift Unlock',
                path: 'gift-unlock/:usageToken',
                component: GiftUnlockComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.GIFT_UNLOCK)],
              },
              {
                title: 'Gift Note',
                path: 'gift-note/:usageToken',
                component: GiftNoteComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.GIFT_NOTE)],
              },
              {
                title: 'Shipping Rates Filter',
                path: 'shipping-rates-filter/:usageToken',
                component: ShippingRatesFilterComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.SHIPPING_RATES_FILTER)],
              },
              {
                title: 'PDQ Cash Back',
                path: 'pdq-cash-back/:usageToken',
                component: HooxPayComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.HOOXPAY)],
              },
              {
                title: 'VIP Process',
                path: 'vip-process/:usageToken',
                component: VipProcessComponent,
              },
              {
                title: 'Discount Rejection Coupon',
                path: 'discount-rejection-coupon/:usageToken',
                component: DiscountRejectionCouponComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.DISCOUNT_REJECTION_COUPON)],
              },
              {
                title: 'Dynamic Reviews',
                path: 'dynamic-reviews/:usageToken',
                component: DynamicReviewsComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.DYNAMIC_REVIEWS)],
              },
              {
                title: 'Product Upgrade',
                path: 'product-upgrade/:usageToken',
                component: ProductUpgradeComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.PRODUCT_UPGRADE)],
              },
              {
                title: 'Urgency Boost',
                path: 'urgency-boost/:usageToken',
                component: UrgencyBoostComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.URGENCY_BOOST)],
              },
              {
                title: 'Multi shipment',
                path: 'multi-shipment/:usageToken',
                component: MultiShipmentComponent,
                canActivate: [
                  extensionTypeAccessGuard(ExtensionComponentType.MULTI_SHIPMENT),
                  () => inject(MultiShipmentEnabledGuard).canActivate(),
                ],
              },
              {
                title: 'Dynamic Extension',
                path: 'dynamic-extension/:usageToken',
                component: DynamicExtensionComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.DYNAMIC_EXTENSION)],
              },
              {
                title: 'OrderProtection.com',
                path: 'order-protection/:usageToken',
                component: OrderProtectionComponent,
                canActivate: [extensionTypeAccessGuard(ExtensionComponentType.ORDER_PROTECTION)],
              },
              {
                path: '**',
                redirectTo: '',
                pathMatch: 'full',
              },
            ],
          },
          { path: '**', redirectTo: '/convert/checkout-groups', pathMatch: 'full' },
        ],
      },
    ]),
  ],
  exports: [RouterModule],
})
export class CheckoutAppRoutingModule {}
